import { Badge, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { formatMarketplaceSentAt } from "../../lib/format-marketplace-timestamp";
import {
  describeDeadline,
  formatYmdLong,
  formatYmdShort,
  orderAgeDays,
} from "../../lib/order-deadline";
import type { DeadlineRollup } from "../../lib/orders-list-model";

interface OrderDeadlineRollupProps {
  rollup: DeadlineRollup;
  todayYmd: string;
  /** ISO instant the order was created. Age is display-only and is not part of the rollup. */
  createdAt: string;
}

/** Parent time aggregate. Overdue uses the error chip so the row wash is not color-only. */
export function OrderDeadlineRollup({
  rollup,
  todayYmd,
  createdAt,
}: OrderDeadlineRollupProps) {
  const { t, i18n } = useTranslation();
  if (!rollup.earliestYmd) {
    return <span className="text-xs text-muted-foreground/65">—</span>;
  }

  const absoluteLong = formatYmdLong(rollup.earliestYmd, i18n.language);
  const absoluteShort = formatYmdShort(
    rollup.earliestYmd,
    todayYmd,
    i18n.language,
  );
  const earliestInfo = describeDeadline(rollup.earliestYmd, todayYmd);
  const isOverdue =
    rollup.overdueCount > 0 ||
    (rollup.source === "order" && earliestInfo?.tone === "overdue");
  const ageDays = orderAgeDays(createdAt);
  const deliveryLine =
    rollup.source === "order"
      ? t("marketplace.orders.list.deadlineRollup.orderTooltip", {
          date: absoluteLong,
        })
      : t("marketplace.orders.list.deadlineRollup.tooltip", {
          count: rollup.scheduledCount,
          date: absoluteLong,
        });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex min-w-0 items-center gap-1.5 whitespace-nowrap">
          {isOverdue ? (
            <Badge variant="status-error" iconStart="alert-circle">
              {ageDays === 0
                ? t("marketplace.orders.list.deadlineRollup.ageLessThanDay")
                : t("marketplace.orders.list.deadlineRollup.age", {
                    count: ageDays,
                  })}
            </Badge>
          ) : earliestInfo?.tone === "today" ? (
            <Badge variant="secondary">
              {t("marketplace.orders.list.deadline.today")}
            </Badge>
          ) : null}
          <span className="truncate text-[11px] tabular-nums text-muted-foreground">
            {t(
              rollup.source === "order"
                ? "marketplace.orders.list.deadlineRollup.target"
                : "marketplace.orders.list.deadlineRollup.earliest",
              { date: absoluteShort },
            )}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <span className="block">{deliveryLine}</span>
        <span className="block">
          {t("marketplace.orders.list.deadlineRollup.createdTooltip", {
            date: formatMarketplaceSentAt(createdAt, i18n.language),
          })}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}
